import { FC } from "react";
import { Button, Grid, GridItem, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, useDisclosure } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { CandidateDetailsForm } from "./CandidateDetailsForm";
import { useCandidate } from "../../api/hooks/useCandidate";
import { useCandidateDelete } from "../../api/hooks/useCandidateDelete";
import { ConfirmationDialog, ConfirmationDialogProps } from "../../components/ConfirmationDialog";
import { useAreaPermissions } from "../../hooks";
import { CandidatesArea } from "../../auth";
import globalEvents from "../../utils/globalEvents";

interface PageWithModalProps {
    returnTo: string;
}

export const CandidateDetailsModal: FC<PageWithModalProps> = ({ returnTo }) => {
    const navigate = useNavigate();
    const { cId, eId, candidateId } = useParams();
    const candidateQuery = useCandidate(cId, eId, candidateId);
    const candidate = candidateQuery.data;
    const refetchCandidateQuery = candidateQuery.refetch;
    globalEvents.registerEvent("refetchCandidateDetailsModalQuery", refetchCandidateQuery);
    const areaPermissions = useAreaPermissions(CandidatesArea, cId);

    // --- Delete Candidate ---
    const navigateToCandidates = () => {
        navigate(`/clients/${cId}/candidates?eId=${eId}`);
    };
    const deleteConfirmation = useDisclosure();
    const deleteCandidate = useCandidateDelete();

    const deleteConfirmationDialog: ConfirmationDialogProps = {
        ...deleteConfirmation,
        title: `Delete Candidate`,
        confirmLabel: "Delete",
        confirmColorScheme: "red",
        onConfirm: async () => {
            try {
                await deleteCandidate.mutateAsync([{ clientId: cId as string, entityId: eId as string, candidateId: candidateId as string }]);
                globalEvents.callAsyncEvent("refetchEntitiesQuery");
                deleteConfirmation.onClose();
                navigateToCandidates();
            } catch (error) {
                console.error(error);
                deleteConfirmation.onClose();
            }
        },
        isDisabled: false,
    };

    // ---- Navigation Helper ----
    const goBackToAllRequests = () => {
        navigate(`/clients/${cId}/candidates/${eId}/${candidateId}/${returnTo}`);
    };

    return (
        <>
            <Modal
                isOpen={true}
                onClose={() => {
                    goBackToAllRequests();
                }}
                size={"4xl"}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Grid gridTemplateColumns={"auto 1fr"} columnGap={"1rem"} rowGap={".25rem"} alignItems={"center"}>
                            <GridItem justifySelf={"start"}>{`${candidate?.id ? "Edit" : "Add"} Candidate`}</GridItem>
                            <GridItem justifySelf={"end"} marginRight={"10pt"}>
                                {areaPermissions.has(CandidatesArea.edit) && (
                                    <Button variant="ghost" onClick={deleteConfirmation.onOpen} colorScheme={"red"}>
                                        Delete Candidate
                                    </Button>
                                )}
                            </GridItem>
                        </Grid>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={"1.5rem"}>
                        <CandidateDetailsForm returnTo={returnTo} />
                    </ModalBody>
                </ModalContent>
            </Modal>
            <ConfirmationDialog {...deleteConfirmationDialog}>
                <p>Are you sure you want to delete this candidate?</p>
                <br />
                <p>This action cannot be undone.</p>
            </ConfirmationDialog>
        </>
    );
};
